import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, useSettings } from '../App';
import axios from 'axios';
import { 
  BarChart3, PieChart as PieChartIcon, LineChart as LineChartIcon, Table2,
  Plus, X, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer
} from 'recharts';

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart', icon: BarChart3 },
  { value: 'pie', label: 'Pie Chart', icon: PieChartIcon },
  { value: 'line', label: 'Line Chart', icon: LineChartIcon },
  { value: 'table', label: 'Table', icon: Table2 },
];

const AGGREGATIONS = ['COUNT', 'SUM', 'AVG'];

const CHART_COLORS = [
  'hsl(var(--primary))', '#1F2937', '#6366F1', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
];

const WidgetDialog = ({ open, onOpenChange, onSaved, editWidget }) => {
  const { settings } = useSettings();

  const [formConfigs, setFormConfigs] = useState([]);
  const [selectedFormConfig, setSelectedFormConfig] = useState('');
  const [fields, setFields] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [showRefInput, setShowRefInput] = useState(false);

  const [title, setTitle] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [axisField, setAxisField] = useState('');
  const [axisAlias, setAxisAlias] = useState('');
  const [legendField, setLegendField] = useState('');
  const [valueConfigs, setValueConfigs] = useState([{ field: '', aggregation: 'COUNT', alias: '' }]);
  const [filters, setFilters] = useState([]);

  const [previewData, setPreviewData] = useState(null);
  const [previewLabels, setPreviewLabels] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef(null);
  const isEditMode = !!editWidget;

  // Load form configs on open
  useEffect(() => {
    if (open) {
      axios.get(`${API}/form-configs?page=1&page_size=100`).then(res => {
        setFormConfigs(res.data.items || []);
      }).catch(() => {});
    }
  }, [open]);

  // Populate form when editing
  useEffect(() => {
    if (open && editWidget) {
      setTitle(editWidget.title || '');
      setChartType(editWidget.chart_type || 'bar');
      setSelectedFormConfig(editWidget.form_config_id || '');
      setAxisField(editWidget.axis_field || '');
      setAxisAlias(editWidget.axis_alias || '');
      setLegendField(editWidget.legend_field || '');
      setValueConfigs(
        editWidget.value_configs?.length > 0
          ? editWidget.value_configs.map(vc => ({ field: vc.field || '', aggregation: vc.aggregation || 'COUNT', alias: vc.alias || '' }))
          : [{ field: '', aggregation: 'COUNT', alias: '' }]
      );
      setFilters(
        editWidget.filters?.length > 0
          ? editWidget.filters.map(f => ({ field: f.field || '', operator: f.operator || 'equals', value: f.value || '' }))
          : []
      );
    } else if (open && !editWidget) {
      resetForm();
    }
  }, [open, editWidget]);

  // Load fields when form config changes
  useEffect(() => {
    if (selectedFormConfig) {
      axios.get(`${API}/dashboard/form-fields/${selectedFormConfig}`).then(res => {
        setFields(res.data.fields || []);
        setDocCount(res.data.document_count || 0);
      }).catch(() => {});
      // Only reset pivot config if NOT in edit mode or if form config actually changed
      if (!isEditMode || (editWidget && editWidget.form_config_id !== selectedFormConfig)) {
        setAxisField('');
        setLegendField('');
        setValueConfigs([{ field: '', aggregation: 'COUNT', alias: '' }]);
        setPreviewData(null);
      }
    }
  }, [selectedFormConfig, isEditMode, editWidget]);

  // REALTIME PREVIEW: auto-fetch when config changes
  useEffect(() => {
    if (!selectedFormConfig || !axisField) {
      return;
    }
    const hasValidValue = valueConfigs.some(vc => vc.field || vc.aggregation === 'COUNT');
    if (!hasValidValue) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPreview();
    }, 400);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [selectedFormConfig, axisField, legendField, valueConfigs, filters, chartType]);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const validValues = valueConfigs.filter(vc => vc.field || vc.aggregation === 'COUNT');
      const res = await axios.post(`${API}/dashboard/widget-preview`, {
        form_config_id: selectedFormConfig,
        axis_field: axisField,
        legend_field: legendField || null,
        value_configs: validValues.map(vc => ({
          field: vc.field || axisField,
          aggregation: vc.aggregation,
          alias: vc.alias || ''
        })),
        filters: filters.filter(f => f.field && f.value)
      });
      setPreviewData(res.data.chart_data || []);
      setPreviewLabels(res.data.value_labels || []);
    } catch {
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Masukkan judul widget'); return; }
    if (!selectedFormConfig || !axisField) { toast.error('Lengkapi konfigurasi'); return; }

    setSaving(true);
    try {
      const validValues = valueConfigs.filter(vc => vc.field || vc.aggregation === 'COUNT');
      const payload = {
        title: title.trim(),
        chart_type: chartType,
        form_config_id: selectedFormConfig,
        axis_field: axisField,
        axis_alias: axisAlias || '',
        legend_field: legendField || null,
        value_configs: validValues.map(vc => ({
          field: vc.field || axisField,
          aggregation: vc.aggregation,
          alias: vc.alias || ''
        })),
        filters: filters.filter(f => f.field && f.value)
      };

      if (isEditMode) {
        await axios.put(`${API}/dashboard/widgets/${editWidget.id}`, payload);
        toast.success('Widget berhasil diperbarui');
      } else {
        await axios.post(`${API}/dashboard/widgets`, payload);
        toast.success('Widget berhasil dibuat');
      }
      resetForm();
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error(isEditMode ? 'Gagal memperbarui widget' : 'Gagal membuat widget');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setChartType('bar');
    setSelectedFormConfig('');
    setAxisField('');
    setAxisAlias('');
    setLegendField('');
    setValueConfigs([{ field: '', aggregation: 'COUNT', alias: '' }]);
    setFilters([]);
    setPreviewData(null);
    setPreviewLabels([]);
    setFields([]);
    setShowRefInput(false);
  };

  const addValueConfig = () => setValueConfigs([...valueConfigs, { field: '', aggregation: 'COUNT', alias: '' }]);
  const removeValueConfig = (i) => { if (valueConfigs.length > 1) setValueConfigs(valueConfigs.filter((_, idx) => idx !== i)); };
  const updateValueConfig = (i, key, value) => { const u = [...valueConfigs]; u[i] = { ...u[i], [key]: value }; setValueConfigs(u); };
  const addFilter = () => setFilters([...filters, { field: '', operator: 'equals', value: '' }]);
  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));
  const updateFilter = (i, key, value) => { const u = [...filters]; u[i] = { ...u[i], [key]: value }; setFilters(u); };

  const numericFields = fields.filter(f => ['Number', 'Currency'].includes(f.field_type));
  const allFieldNames = fields.map(f => f.name);

  const renderPreviewChart = () => {
    if (loadingPreview) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="ml-2 text-sm text-gray-500">Memuat preview...</span>
        </div>
      );
    }
    if (!previewData || previewData.length === 0) {
      return <p className="text-sm text-gray-500 text-center py-8">Tidak ada data untuk ditampilkan</p>;
    }

    if (chartType === 'table') {
      return (
        <div className="overflow-auto max-h-48 rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{axisAlias || axisField}</th>
                {previewLabels.map((label, i) => (
                  <th key={i} className="px-3 py-2 text-right font-medium text-gray-700">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, ri) => (
                <tr key={ri} className="border-t">
                  <td className="px-3 py-2 font-medium">{row.category}</td>
                  {previewLabels.map((label, i) => (
                    <td key={i} className="px-3 py-2 text-right">{row[label] ?? 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (chartType === 'pie') {
      const dataKey = previewLabels[0];
      return (
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={previewData} dataKey={dataKey} nameKey="category" cx="50%" cy="50%" outerRadius={70} label>
              {previewData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {previewLabels.map((label, i) => (
              <Line key={i} type="monotone" dataKey={label} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={previewData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {previewLabels.map((label, i) => (
            <Bar key={i} dataKey={label} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="widget-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold font-['Outfit']">
            {isEditMode ? 'Edit Widget' : 'Buat Widget Baru'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Title & Chart Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Judul Widget</Label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Contoh: Dokumen per Kategori"
                data-testid="widget-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tipe Grafik</Label>
              <div className="flex gap-2">
                {CHART_TYPES.map(ct => {
                  const Icon = ct.icon;
                  return (
                    <button
                      key={ct.value}
                      onClick={() => setChartType(ct.value)}
                      data-testid={`chart-type-${ct.value}`}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                        chartType === ct.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} />
                      {ct.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Config Selection */}
          <div className="space-y-1.5">
            <Label>Sumber Data (Form Config)</Label>
            <Select value={selectedFormConfig} onValueChange={setSelectedFormConfig}>
              <SelectTrigger data-testid="form-config-select">
                <SelectValue placeholder="Pilih form config..." />
              </SelectTrigger>
              <SelectContent>
                {formConfigs.map(fc => (
                  <SelectItem key={fc.id} value={fc.id}>{fc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedFormConfig && (
              <p className="text-xs text-gray-500">{docCount} dokumen tersedia</p>
            )}
          </div>

          {/* Available Fields & Reference Input */}
          {fields.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Fields yang Tersedia</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRefInput(!showRefInput)}
                  className="gap-1 text-xs"
                  data-testid="toggle-ref-input"
                >
                  <Info size={14} />
                  Referensi Input
                  {showRefInput ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {fields.map(f => (
                  <Badge key={f.name} variant="outline" className="text-xs">
                    {f.label} <span className="text-gray-400 ml-1">({f.field_type})</span>
                  </Badge>
                ))}
              </div>

              {showRefInput && (
                <div className="mt-2 border rounded-md overflow-auto max-h-48 bg-gray-50/50">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Field</th>
                        <th className="px-2 py-1.5 text-left font-medium">Type</th>
                        <th className="px-2 py-1.5 text-left font-medium">Sample Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map(f => (
                        <tr key={f.name} className="border-t">
                          <td className="px-2 py-1.5 font-medium">{f.label}</td>
                          <td className="px-2 py-1.5 text-gray-500">{f.field_type}</td>
                          <td className="px-2 py-1.5 text-gray-600">
                            {f.samples?.length > 0 ? f.samples.join(', ') : <span className="italic text-gray-400">Tidak ada data</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Pivot Chart Areas + Realtime Preview */}
          {fields.length > 0 && (
            <div className="grid grid-cols-5 gap-4">
              {/* Left: Pivot Config (3 cols) */}
              <div className="col-span-3 border rounded-lg p-4 space-y-4 bg-gray-50/30" style={{ borderStyle: 'dashed' }}>
                <h3 className="text-sm font-semibold text-gray-700">PivotChart Areas</h3>

                <div className="grid grid-cols-2 gap-3">
                  {/* Filters */}
                  <div className="space-y-2 border rounded-md p-3 bg-white">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Filters</Label>
                      <Button variant="ghost" size="sm" onClick={addFilter} className="h-6 w-6 p-0" data-testid="add-filter-btn">
                        <Plus size={14} />
                      </Button>
                    </div>
                    {filters.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">Klik + untuk menambah filter...</p>
                    ) : (
                      <div className="space-y-2">
                        {filters.map((f, i) => (
                          <div key={i} className="flex gap-1.5 items-center">
                            <Select value={f.field} onValueChange={v => updateFilter(i, 'field', v)}>
                              <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Field..." /></SelectTrigger>
                              <SelectContent>{allFieldNames.map(fn => <SelectItem key={fn} value={fn}>{fn}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={f.operator} onValueChange={v => updateFilter(i, 'operator', v)}>
                              <SelectTrigger className="h-7 text-xs w-20"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="equals">=</SelectItem>
                                <SelectItem value="not_equals">!=</SelectItem>
                                <SelectItem value="contains">~</SelectItem>
                                <SelectItem value="gt">&gt;</SelectItem>
                                <SelectItem value="lt">&lt;</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input className="h-7 text-xs flex-1" value={f.value} onChange={e => updateFilter(i, 'value', e.target.value)} placeholder="Value..." />
                            <Button variant="ghost" size="sm" onClick={() => removeFilter(i)} className="h-6 w-6 p-0 text-red-500"><X size={12} /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Legend */}
                  <div className="space-y-2 border rounded-md p-3 bg-white">
                    <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Legend (Series)</Label>
                    <Select value={legendField || 'none'} onValueChange={v => setLegendField(v === 'none' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="legend-field-select"><SelectValue placeholder="Tidak ada" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Tidak ada</SelectItem>
                        {allFieldNames.map(fn => <SelectItem key={fn} value={fn}>{fn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Axis */}
                  <div className="space-y-2 border rounded-md p-3 bg-white">
                    <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Axis (Categories)</Label>
                    <Select value={axisField} onValueChange={setAxisField}>
                      <SelectTrigger className="h-8 text-xs" data-testid="axis-field-select"><SelectValue placeholder="Pilih field untuk axis..." /></SelectTrigger>
                      <SelectContent>{allFieldNames.map(fn => <SelectItem key={fn} value={fn}>{fn}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-7 text-xs" value={axisAlias} onChange={e => setAxisAlias(e.target.value)} placeholder="Custom alias (opsional)" />
                  </div>

                  {/* Values */}
                  <div className="space-y-2 border rounded-md p-3 bg-white">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Values</Label>
                      <Button variant="ghost" size="sm" onClick={addValueConfig} className="h-6 w-6 p-0" data-testid="add-value-btn"><Plus size={14} /></Button>
                    </div>
                    <div className="space-y-2">
                      {valueConfigs.map((vc, i) => (
                        <div key={i} className="flex gap-1.5 items-center p-1.5 rounded bg-primary/5 border border-primary/20">
                          <Select value={vc.aggregation} onValueChange={v => updateValueConfig(i, 'aggregation', v)}>
                            <SelectTrigger className="h-7 text-xs w-20 font-medium"><SelectValue /></SelectTrigger>
                            <SelectContent>{AGGREGATIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                          </Select>
                          <span className="text-xs text-gray-500">of</span>
                          <Select value={vc.field} onValueChange={v => updateValueConfig(i, 'field', v)}>
                            <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Field..." /></SelectTrigger>
                            <SelectContent>
                              {(vc.aggregation === 'COUNT' ? allFieldNames : numericFields.map(f => f.name)).map(fn => (
                                <SelectItem key={fn} value={fn}>{fn}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input className="h-7 text-xs w-20" value={vc.alias} onChange={e => updateValueConfig(i, 'alias', e.target.value)} placeholder="Alias" />
                          {valueConfigs.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeValueConfig(i)} className="h-6 w-6 p-0 text-red-500"><X size={12} /></Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Realtime Preview (2 cols) */}
              <div className="col-span-2 border rounded-lg p-4 bg-white flex flex-col">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Simulasi Chart (Realtime)</h3>
                <div className="flex-1 min-h-[200px]">
                  {!axisField ? (
                    <p className="text-xs text-gray-400 text-center mt-12">Pilih axis field untuk melihat simulasi</p>
                  ) : (
                    renderPreviewChart()
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !title || !selectedFormConfig || !axisField}
            data-testid="submit-widget-btn"
          >
            {saving ? 'Menyimpan...' : isEditMode ? 'Simpan Perubahan' : 'Simpan Widget'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WidgetDialog;
